import { AdControls } from '@post-office/components--ad-controls';
import { cssMap, cx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { useMessageEvent } from '@post-office/message-lifecycle';
import { useCallback, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { type JSX } from 'react/jsx-runtime';

import {
	ROVO_JIRA_TWG_CLI_CTA_EXPERIMENT,
	ROVO_JIRA_TWG_CLI_CTA_TREATMENT_PARAM,
	RovoJiraTwgCliCtaCohort,
} from '../constants';
import { messages } from '../messages';
import { TeamworkGraphIcon } from './TeamworkGraphIcon';

const styles = cssMap({
	boxContainer: {
		display: 'flex',
		width: '100%',
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		alignItems: 'flex-start',
		gap: token('space.100'),
		borderRadius: token('radius.large'),
		backgroundColor: token('color.background.information'),
		placeContent: 'center',
	},
	contentStack: {
		gap: token('space.100'),
		width: '100%',
	},
	headerRow: {
		justifyContent: 'space-between',
		alignItems: 'center',
		width: '100%',
	},
	ctaHeading: {
		font: token('font.heading.xsmall'),
		margin: token('space.0'),
	},
	ctaLink: {
		backgroundColor: token('color.background.neutral.subtle'),
		padding: token('space.0'),
		borderRadius: token('radius.xsmall'),
		color: token('color.link'),
		cursor: 'pointer',
		font: token('font.body.small'),
		textAlign: 'left',
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'underline',
		},
		'&:active': {
			color: token('color.link.pressed'),
		},
	},
});

const ACTION_SUBJECT = 'rovoJiraTwgCliCta';
const linkUrl =
	'https://teamworkgraph.com/?utm_source=jira&utm_medium=cta&utm_campaign=jira_side_nav#cli';

type JiraTwgCtaMessageProps = {
	context: {
		source: string;
	};
	handleOnDismissed: () => void;
};

export const JiraTwgCtaMessage = ({
	context,
	handleOnDismissed,
}: JiraTwgCtaMessageProps): JSX.Element | null => {
	const { source } = context;

	// Exposure is already logged by filterExperimentalMessages in the placement.
	const experimentCohort = FeatureGates.getExperimentValue(
		ROVO_JIRA_TWG_CLI_CTA_EXPERIMENT,
		ROVO_JIRA_TWG_CLI_CTA_TREATMENT_PARAM,
		RovoJiraTwgCliCtaCohort.CONTROL as RovoJiraTwgCliCtaCohort,
		{ fireExperimentExposure: false },
	);

	const { formatMessage } = useIntl();
	const { messageClicked } = useMessageEvent();
	const { messageInstanceClicked } = useMessageInstanceEvent();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

	useEffect(() => {
		if (experimentCohort === RovoJiraTwgCliCtaCohort.CONTROL) return;
		fireAnalyticsEvent({
			eventType: 'track',
			action: 'rendered',
			actionSubject: ACTION_SUBJECT,
			attributes: {
				variant: experimentCohort,
				source,
			},
		});
	}, [fireAnalyticsEvent, experimentCohort, source]);

	const handleDismiss = useCallback(() => {
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'dismissed',
			actionSubject: ACTION_SUBJECT,
			attributes: {
				variant: experimentCohort,
				source,
			},
		});
		void messageClicked();
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'dismiss',
				clickedElement: 'button',
				buttonType: 'secondary',
			},
			action: {
				actionType: 'dismiss',
			},
			additionalMetadataFromFrontend: {
				experimentCohort,
				source,
			},
		});
		handleOnDismissed();
	}, [
		fireAnalyticsEvent,
		experimentCohort,
		source,
		messageClicked,
		messageInstanceClicked,
		handleOnDismissed,
	]);

	const handleLinkClick = useCallback(() => {
		void messageClicked();
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'installLink',
				clickedElement: 'link',
			},
			action: {
				actionType: 'open',
				destinationUrl: linkUrl,
			},
			additionalMetadataFromFrontend: {
				experimentCohort,
				source,
			},
		});
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: ACTION_SUBJECT,
			attributes: {
				variant: experimentCohort,
				source,
			},
		});
	}, [experimentCohort, source, messageClicked, messageInstanceClicked, linkUrl]);

	if (experimentCohort === RovoJiraTwgCliCtaCohort.CONTROL) {
		return null;
	}

	return (
		<Box xcss={cx(styles.boxContainer)} testId="rovo-jira-twg-cli-cta">
			<Stack xcss={cx(styles.contentStack)} testId="rovo-jira-twg-cli-cta-content">
				<Inline xcss={cx(styles.headerRow)}>
					<TeamworkGraphIcon />
					<AdControls
						showWhyAmISeeingThis={true}
						whyAmISeeingThisModalContent={{
							reasons: [<FormattedMessage key="reason-1" {...messages.whyAmISeeingThisReason} />],
						}}
						onDismissed={handleDismiss}
						spacing="compact"
					/>
				</Inline>
				<Box as="h3" xcss={cx(styles.ctaHeading)}>
					{formatMessage(messages.ctaTitle)}
				</Box>
				<Text size="small" color="color.text.subtlest">
					{formatMessage(messages.ctaDescription)}
				</Text>
				<Pressable
					onClick={handleLinkClick}
					testId="rovo-jira-twg-cli-cta-link"
					xcss={cx(styles.ctaLink)}
				>
					{formatMessage(messages.ctaLink)}
				</Pressable>
			</Stack>
		</Box>
	);
};
